import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  LayoutDashboard, BookOpen, Users, Send, LogOut, Save, User, Shield, RefreshCw, Lock, Eye, EyeOff,
  Calendar, Clock, Video, FileText, CreditCard, DollarSign, Link, Mail, MailOpen, Trash2, Search,
  Filter, ChevronDown, CheckCircle, XCircle, Tag, Bell, AlertCircle, Menu, X, Sparkles, UserPlus, Edit,
  Database, Download, Upload, HardDrive, AlertTriangle, Star, EyeOff as Hide
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { ScrollArea } from "../components/ui/scroll-area";
import { Badge } from "../components/ui/badge";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("dashboard");
  
  const [courseInfo, setCourseInfo] = useState({
    title: "وعي الغذاء صلاة",
    description: "",
    start_date: "٣٠ أيار",
    duration: "من ساعتين إلى ساعتين ونصف",
    sessions: "٤",
    schedule: "كل يوم الساعة ٣ ظهراً بتوقيت غرينتش",
    price: "499",
    currency: "USD",
    status: "coming_soon"
  });
  const [savingCourse, setSavingCourse] = useState(false);
  const [users, setUsers] = useState([]);
  const [userFilter, setUserFilter] = useState("all"); // all | paid | unpaid
  const [userSearch, setUserSearch] = useState("");
  const [advFiltersOpen, setAdvFiltersOpen] = useState(false);
  const [advFilters, setAdvFilters] = useState({
    provider: "all",        // all | stripe | paypal | manual | none
    manualMethod: "all",    // all | cash | bank_transfer | stripe_external | paypal_external | other
    country: "all",
    paidFrom: "",
    paidTo: "",
    createdFrom: "",
    createdTo: "",
    amountMin: "",
    amountMax: "",
    hasPhone: "all",        // all | yes | no
    role: "all",            // all | user | admin
    enrolled: "all",        // all | yes | no
    sortBy: "created_at",   // name | email | paid_at | created_at | amount_paid
    sortDir: "desc",        // asc | desc
  });
  const resetAdvFilters = () => setAdvFilters({
    provider: "all", manualMethod: "all", country: "all",
    paidFrom: "", paidTo: "", createdFrom: "", createdTo: "",
    amountMin: "", amountMax: "", hasPhone: "all", role: "all",
    enrolled: "all", sortBy: "created_at", sortDir: "desc",
  });
  const [showAddUser, setShowAddUser] = useState(false);
  const [editUserId, setEditUserId] = useState(null);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const emptyUserForm = {
    name: "", email: "", phone: "", country: "", notes: "",
    payment_type: "paid", amount: "499", method: "cash", reference: "", paid_at: "",
    send_welcome: true,
  };
  const [userForm, setUserForm] = useState(emptyUserForm);
  const [userFormSubmitting, setUserFormSubmitting] = useState(false);
  const [messageForm, setMessageForm] = useState({ recipient_id: "all", subject: "", content: "", category: "zoom" });
  const [sendingMessage, setSendingMessage] = useState(false);
  const [sentMessages, setSentMessages] = useState([]);
  const [selectedSentMessage, setSelectedSentMessage] = useState(null);
  const [sentSearchQuery, setSentSearchQuery] = useState("");
  const [sentFilterCategory, setSentFilterCategory] = useState("all");
  const [deleteMessageDialogOpen, setDeleteMessageDialogOpen] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);

  // Backup / Restore
  const [backupStats, setBackupStats] = useState(null);
  const [backupLoading, setBackupLoading] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importMode, setImportMode] = useState("merge"); // merge | replace
  const [importing, setImporting] = useState(false);
  const [importReport, setImportReport] = useState(null);
  const [confirmReplaceOpen, setConfirmReplaceOpen] = useState(false);

  useEffect(() => { checkAdminAuth(); }, []);
  useEffect(() => { if (isAuthenticated) { fetchCourseInfo(); fetchUsers(); fetchSentMessages(); } }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && activeTab === "backup") fetchBackupStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, activeTab]);

  const checkAdminAuth = async () => {
    try { await axios.get(`${API}/admin/verify`, { withCredentials: true }); setIsAuthenticated(true); }
    catch (error) { setIsAuthenticated(false); }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      await axios.post(`${API}/admin/login`, { password }, { withCredentials: true });
      toast.success("تم تسجيل الدخول بنجاح!");
      setIsAuthenticated(true);
    } catch (error) { toast.error("كلمة المرور غير صحيحة"); setPassword(""); }
    finally { setLoginLoading(false); }
  };

  const handleLogout = async () => {
    try { await axios.post(`${API}/admin/logout`, {}, { withCredentials: true }); toast.success("تم تسجيل الخروج"); setIsAuthenticated(false); setPassword(""); }
    catch (error) { console.error("Logout error:", error); }
  };

  const fetchCourseInfo = async () => {
    try { const response = await axios.get(`${API}/course-info`); if (response.data) setCourseInfo(prev => ({ ...prev, ...response.data })); }
    catch (error) { console.log("No course info yet"); }
  };

  const fetchUsers = async () => {
    try { const response = await axios.get(`${API}/admin/users`, { withCredentials: true }); setUsers(response.data); }
    catch (error) { toast.error("فشل تحميل المستخدمين"); }
  };

  const openAddUser = () => {
    setEditUserId(null);
    setUserForm({ ...emptyUserForm });
    setShowAddUser(true);
  };

  const openEditUser = (u) => {
    setEditUserId(u.user_id);
    const currentType = u.paid ? "paid" : "none";
    setUserForm({
      name: u.name || "",
      email: u.email || "",
      phone: u.phone || "",
      country: u.country || "",
      notes: u.notes || "",
      payment_type: currentType,
      _initial_payment_type: currentType,        // track if changed
      amount: u.amount_paid != null ? String(u.amount_paid) : "499",
      method: u.payment_method || "cash",
      reference: u.payment_reference || "",
      paid_at: "",
      send_welcome: false,
    });
    setShowAddUser(true);
  };

  const submitUserForm = async () => {
    if (!userForm.name.trim() || !userForm.email.trim()) {
      toast.error("الاسم والبريد الإلكتروني مطلوبان");
      return;
    }
    setUserFormSubmitting(true);
    try {
      if (editUserId) {
        // 1. Update basic info
        await axios.patch(`${API}/admin/users/${editUserId}`, {
          name: userForm.name.trim(),
          email: userForm.email.trim(),
          phone: userForm.phone || null,
          country: userForm.country || null,
          notes: userForm.notes || null,
        }, { withCredentials: true });

        // 2. Update payment status if changed
        if (userForm.payment_type !== userForm._initial_payment_type) {
          const payment = {
            type: userForm.payment_type,
            amount: userForm.payment_type === "paid" ? Number(userForm.amount || 0) : null,
            method: userForm.payment_type === "none" ? null : userForm.method,
            reference: userForm.reference || null,
            paid_at: userForm.paid_at || null,
          };
          await axios.post(`${API}/admin/users/${editUserId}/payment`, {
            name: userForm.name.trim(),
            email: userForm.email.trim(),
            payment,
            send_welcome_message: !!userForm.send_welcome,
          }, { withCredentials: true });
          const lbl = userForm.payment_type === "paid" ? "مدفوع" : "غير مدفوع";
          toast.success(`تم تحديث المستخدم وتحويل الحالة إلى: ${lbl}`);
        } else {
          toast.success("تم تحديث المستخدم");
        }
      } else {
        const payment = {
          type: userForm.payment_type,
          amount: userForm.payment_type === "paid" ? Number(userForm.amount || 0) : null,
          method: userForm.payment_type === "none" ? null : userForm.method,
          reference: userForm.reference || null,
          paid_at: userForm.paid_at || null,
        };
        await axios.post(`${API}/admin/users/manual`, {
          name: userForm.name.trim(),
          email: userForm.email.trim(),
          phone: userForm.phone || null,
          country: userForm.country || null,
          notes: userForm.notes || null,
          payment,
          send_welcome_message: !!userForm.send_welcome,
        }, { withCredentials: true });
        toast.success("تم إضافة المستخدم");
      }
      setShowAddUser(false);
      await fetchUsers();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "فشل الحفظ");
    } finally {
      setUserFormSubmitting(false);
    }
  };

  const confirmDeleteUser = async () => {
    if (!deleteUserId) return;
    try {
      await axios.delete(`${API}/admin/users/${deleteUserId}`, { withCredentials: true });
      toast.success("تم حذف المستخدم");
      setDeleteUserId(null);
      await fetchUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الحذف");
    }
  };

  const fetchSentMessages = async () => {
    try { const response = await axios.get(`${API}/admin/messages`, { withCredentials: true }); setSentMessages(response.data); }
    catch (error) { console.error("Failed to fetch sent messages:", error); }
  };

  const handleDeleteSentMessage = async () => {
    if (!messageToDelete) return;
    try {
      await axios.delete(`${API}/admin/messages/${messageToDelete}`, { withCredentials: true });
      setSentMessages(sentMessages.filter(m => m.message_id !== messageToDelete));
      if (selectedSentMessage?.message_id === messageToDelete) setSelectedSentMessage(null);
      toast.success("تم حذف الرسالة");
    } catch (error) { toast.error("فشل حذف الرسالة"); }
    finally { setDeleteMessageDialogOpen(false); setMessageToDelete(null); }
  };

  const getFilteredSentMessages = () => {
    let filtered = sentMessages;
    if (sentFilterCategory !== "all") filtered = filtered.filter(m => m.category === sentFilterCategory);
    if (sentSearchQuery.trim()) {
      const query = sentSearchQuery.toLowerCase();
      filtered = filtered.filter(m => m.subject.toLowerCase().includes(query) || m.content.toLowerCase().includes(query));
    }
    return filtered;
  };

  const getCategoryBadge = (category) => {
    const colors = { general: "bg-gray-100 text-gray-800", announcement: "bg-blue-100 text-blue-800", update: "bg-green-100 text-green-800", important: "bg-red-100 text-red-800" };
    return colors[category] || colors.general;
  };

  const getCategoryLabel = (category) => {
    const labels = { general: "عام", announcement: "إعلان", update: "تحديث", important: "مهم" };
    return labels[category] || "عام";
  };

  const getCategoryIcon = (category) => {
    switch(category) {
      case 'announcement': return <Bell className="w-3 h-3" />;
      case 'update': return <RefreshCw className="w-3 h-3" />;
      case 'important': return <AlertCircle className="w-3 h-3" />;
      default: return <Tag className="w-3 h-3" />;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getRecipientName = (recipientId) => {
    if (recipientId === "all") return "جميع المستخدمين (بث عام)";
    const user = users.find(u => u.user_id === recipientId);
    return user ? user.name : recipientId;
  };

  const handleSaveCourseInfo = async (e) => {
    e.preventDefault();
    setSavingCourse(true);
    try { await axios.put(`${API}/admin/course-info`, courseInfo, { withCredentials: true }); toast.success("تم حفظ معلومات الدورة!"); }
    catch (error) { toast.error("فشل حفظ المعلومات"); }
    finally { setSavingCourse(false); }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageForm.subject.trim() || !messageForm.content.trim()) { toast.error("يرجى ملء جميع الحقول"); return; }
    setSendingMessage(true);
    try {
      const response = await axios.post(`${API}/admin/messages`, messageForm, { withCredentials: true });
      toast.success("تم إرسال الرسالة بنجاح");
      setMessageForm({ recipient_id: "all", subject: "", content: "", category: "zoom" });
      fetchSentMessages();
    } catch (error) { toast.error(error.response?.data?.detail || "فشل إرسال الرسالة"); }
    finally { setSendingMessage(false); }
  };

  const handleTabChange = (tab) => { setActiveTab(tab); setSidebarOpen(false); };

  // ─── Backup / Restore helpers ──────────────────────────
  const fetchBackupStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/backup/stats`, { withCredentials: true });
      setBackupStats(res.data);
    } catch (e) {
      console.error("backup stats error:", e);
      toast.error("تعذّر تحميل إحصائيات قاعدة البيانات");
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  const exportBackup = async (collection = null) => {
    setBackupLoading(true);
    const toastId = toast.loading(collection ? `جارٍ تصدير ${collection}...` : "جارٍ تصدير قاعدة البيانات بالكامل...");
    try {
      const url = collection ? `${API}/admin/backup/export/${collection}` : `${API}/admin/backup/export`;
      const res = await axios.get(url, { withCredentials: true, responseType: "blob" });
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      const fname = collection ? `backup_${collection}_${ts}.json` : `backup_full_${ts}.json`;
      downloadBlob(new Blob([res.data], { type: "application/json" }), fname);
      toast.success("تم التصدير بنجاح ✨", { id: toastId });
    } catch (e) {
      console.error("export error:", e);
      toast.error("تعذّر التصدير", { id: toastId });
    } finally {
      setBackupLoading(false);
    }
  };

  const performImport = async () => {
    if (!importFile) { toast.error("اختر ملف JSON أولاً"); return; }
    setImporting(true);
    setImportReport(null);
    const toastId = toast.loading(importMode === "replace" ? "جارٍ الاستبدال الكامل..." : "جارٍ دمج البيانات...");
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      fd.append("mode", importMode);
      const res = await axios.post(`${API}/admin/backup/import`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportReport(res.data);
      toast.success("تم الاستيراد بنجاح ✅", { id: toastId });
      // Refresh in-memory state
      await Promise.all([fetchUsers(), fetchSentMessages(), fetchCourseInfo(), fetchBackupStats()]);
      setImportFile(null);
      const fileInput = document.getElementById("backup-import-file");
      if (fileInput) fileInput.value = "";
    } catch (e) {
      console.error("import error:", e);
      toast.error(e?.response?.data?.detail || "فشل الاستيراد", { id: toastId });
    } finally {
      setImporting(false);
      setConfirmReplaceOpen(false);
    }
  };
  // ────────────────────────────────────────────────────────

  const stats = [
    { label: "المستخدمون المسجلون", value: users.length, icon: Users, color: "from-[#10B981] to-[#34D399]" },
    { label: "حالة الدورة", value: courseInfo.status === 'coming_soon' ? 'قريبًا' : courseInfo.status === 'open' ? 'مفتوح' : 'مغلق', icon: BookOpen, color: "from-[#7C3AED] to-[#A78BFA]" },
  ];

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFBFF]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7C3AED] mx-auto mb-4"></div>
          <p className="text-[#4A5078]">جاري التحقق...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFBFF] p-4" data-testid="admin-login-page">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-[#7C3AED] to-[#EC4899] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-purple-500/20">
                <Lock className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-[#1A1D3D]" style={{ fontFamily: 'Cairo, sans-serif' }}>لوحة الإدارة</h1>
              <p className="text-[#4A5078] mt-2">أدخل كلمة المرور للوصول</p>
            </div>
            <form onSubmit={handleLogin}>
              <div className="mb-6">
                <Label htmlFor="password" className="mb-2 block">كلمة المرور</Label>
                <div className="relative">
                  <Input id="password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="أدخل كلمة المرور" className="pl-10" data-testid="admin-password-input" autoFocus />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8B92B3] hover:text-[#4A5078]">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" className="w-full bg-gradient-to-r from-[#7C3AED] to-[#EC4899] hover:from-[#6D28D9] hover:to-[#DB2777]" disabled={loginLoading || !password} data-testid="admin-login-btn">
                {loginLoading ? (<><RefreshCw className="w-4 h-4 ml-2 animate-spin" />جاري الدخول...</>) : (<><Lock className="w-4 h-4 ml-2" />تسجيل الدخول</>)}
              </Button>
            </form>
            <div className="mt-6 text-center">
              <button onClick={() => navigate('/')} className="text-sm text-[#7C3AED] hover:underline">← العودة للموقع</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFBFF]" data-testid="admin-panel">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-[#E2E8F0] z-50 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-br from-[#7C3AED] to-[#EC4899] rounded-lg flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <span className="font-bold text-[#1A1D3D] text-sm" style={{ fontFamily: 'Cairo, sans-serif' }}>لوحة الإدارة</span>
        </div>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-[#F0F4FF] rounded-lg">
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {sidebarOpen && <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setSidebarOpen(false)} />}

      {/* Sidebar - RTL: right side */}
      <aside className={`fixed right-0 top-0 h-full w-64 bg-white border-l border-[#E2E8F0] z-50 transform transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : 'translate-x-full'} lg:translate-x-0`}>
        <div className="p-6 pt-6 lg:pt-6">
          <div className="hidden lg:flex items-center gap-2 mb-2">
            <div className="w-9 h-9 bg-gradient-to-br from-[#7C3AED] to-[#EC4899] rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/20">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-[#1A1D3D]" style={{ fontFamily: 'Cairo, sans-serif' }}>وعي الغذاء صلاة</span>
          </div>
          <div className="lg:hidden h-10"></div>
          <span className="badge-info text-xs">لوحة الإدارة</span>

          <nav className="mt-8 space-y-2">
            <button onClick={() => handleTabChange("dashboard")} className={`sidebar-link w-full ${activeTab === "dashboard" ? "active" : ""}`} data-testid="admin-nav-dashboard">
              <LayoutDashboard className="w-5 h-5" /><span>لوحة التحكم</span>
            </button>
            <button onClick={() => handleTabChange("course")} className={`sidebar-link w-full ${activeTab === "course" ? "active" : ""}`} data-testid="admin-nav-course">
              <BookOpen className="w-5 h-5" /><span>معلومات الدورة</span>
            </button>
            <button onClick={() => handleTabChange("users")} className={`sidebar-link w-full ${activeTab === "users" ? "active" : ""}`} data-testid="admin-nav-users">
              <Users className="w-5 h-5" /><span>المستخدمون</span>
            </button>
            <button onClick={() => handleTabChange("messages")} className={`sidebar-link w-full ${activeTab === "messages" ? "active" : ""}`} data-testid="admin-nav-messages">
              <Video className="w-5 h-5" /><span>إرسال روابط Zoom</span>
            </button>
            <button onClick={() => handleTabChange("sent")} className={`sidebar-link w-full ${activeTab === "sent" ? "active" : ""}`} data-testid="admin-nav-sent">
              <Mail className="w-5 h-5" /><span>روابط مُرسَلة</span>
              {sentMessages.length > 0 && <span className="mr-auto text-xs text-[#8B92B3]">{sentMessages.length}</span>}
            </button>
            <button onClick={() => handleTabChange("backup")} className={`sidebar-link w-full ${activeTab === "backup" ? "active" : ""}`} data-testid="admin-nav-backup">
              <Database className="w-5 h-5" /><span>نسخ احتياطي / استيراد</span>
            </button>
            <button onClick={() => handleTabChange("reviews")} className={`sidebar-link w-full ${activeTab === "reviews" ? "active" : ""}`} data-testid="admin-nav-reviews">
              <Star className="w-5 h-5" /><span>التقييمات</span>
            </button>
          </nav>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-[#E2E8F0]">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-[#7C3AED] to-[#EC4899] rounded-full flex items-center justify-center shadow-lg shadow-purple-500/20">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-[#1A1D3D]">المسؤول</p>
              <p className="text-xs text-[#8B92B3]">صلاحيات كاملة</p>
            </div>
          </div>
          <Button onClick={handleLogout} variant="outline" className="w-full border-[#E2E8F0] hover:bg-gradient-to-r hover:from-[#F0F4FF] hover:to-[#FCE7F3] hover:border-[#7C3AED] text-[#4A5078]" data-testid="admin-logout-button">
            <LogOut className="w-4 h-4 ml-2" />تسجيل الخروج
          </Button>
        </div>
      </aside>

      {/* Main Content - RTL: margin-right */}
      <main className="lg:mr-64 p-4 lg:p-6 pt-20 lg:pt-6">
        {activeTab === "dashboard" && (
          <div data-testid="admin-dashboard-section">
            <div className="mb-6 lg:mb-8">
              <h1 className="heading-2 mb-2 text-xl lg:text-3xl">لوحة التحكم</h1>
              <p className="body-text text-sm lg:text-base">إدارة دورة وعي الغذاء صلاة.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6 mb-6 lg:mb-8">
              {stats.map((stat, index) => (
                <div key={index} className="card-dashboard p-4 lg:p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-small mb-1 text-xs lg:text-sm">{stat.label}</p>
                      <p className="text-2xl lg:text-3xl font-bold text-[#1A1D3D]">{stat.value}</p>
                    </div>
                    <div className={`w-10 h-10 lg:w-12 lg:h-12 bg-gradient-to-br ${stat.color} rounded-xl flex items-center justify-center shadow-lg`}>
                      <stat.icon className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="card-dashboard p-4 lg:p-6">
              <h3 className="heading-4 mb-4 text-base lg:text-lg">إجراءات سريعة</h3>
              <div className="flex flex-col sm:flex-row gap-3 lg:gap-4">
                <Button onClick={() => handleTabChange("course")} className="bg-gradient-to-r from-[#7C3AED] to-[#EC4899] hover:from-[#6D28D9] hover:to-[#DB2777] shadow-lg shadow-purple-500/25 w-full sm:w-auto">
                  <BookOpen className="w-4 h-4 ml-2" />تعديل الدورة
                </Button>
                <Button onClick={() => handleTabChange("messages")} variant="outline" className="border-[#E2E8F0] hover:border-[#7C3AED] hover:bg-[#F0F4FF] w-full sm:w-auto">
                  <Video className="w-4 h-4 ml-2" />إرسال رابط Zoom
                </Button>
              </div>
            </div>
          </div>
        )}

        {activeTab === "course" && (
          <div data-testid="admin-course-section">
            <div className="mb-6 lg:mb-8">
              <h1 className="heading-2 mb-2 text-xl lg:text-3xl">وعي الغذاء صلاة</h1>
              <p className="body-text text-sm lg:text-base">تحديث معلومات الدورة المعروضة على الموقع.</p>
            </div>
            <div className="card-dashboard p-4 lg:p-6 max-w-2xl">
              <form onSubmit={handleSaveCourseInfo} className="space-y-4 lg:space-y-6">
                <div>
                  <Label htmlFor="description" className="mb-2 block flex items-center gap-2 text-sm"><FileText className="w-4 h-4" />وصف الدورة</Label>
                  <Textarea id="description" value={courseInfo.description} onChange={(e) => setCourseInfo({ ...courseInfo, description: e.target.value })} placeholder="وصف ما سيتعلمه المشاركون..." rows={4} className="resize-none text-sm" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="start_date" className="mb-2 block flex items-center gap-2 text-sm"><Calendar className="w-4 h-4" />تاريخ البداية</Label>
                    <Input id="start_date" value={courseInfo.start_date} onChange={(e) => setCourseInfo({ ...courseInfo, start_date: e.target.value })} placeholder="مثال: ٣٠ أيار" className="text-sm" />
                  </div>
                  <div>
                    <Label htmlFor="duration" className="mb-2 block flex items-center gap-2 text-sm"><Clock className="w-4 h-4" />المدة</Label>
                    <Input id="duration" value={courseInfo.duration} onChange={(e) => setCourseInfo({ ...courseInfo, duration: e.target.value })} placeholder="مثال: ساعتين ونصف" className="text-sm" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="sessions" className="mb-2 block flex items-center gap-2 text-sm"><Video className="w-4 h-4" />عدد الجلسات</Label>
                    <Input id="sessions" value={courseInfo.sessions} onChange={(e) => setCourseInfo({ ...courseInfo, sessions: e.target.value })} placeholder="مثال: ٤" className="text-sm" />
                  </div>
                  <div>
                    <Label htmlFor="status" className="mb-2 block text-sm">حالة الدورة</Label>
                    <Select value={courseInfo.status} onValueChange={(value) => setCourseInfo({ ...courseInfo, status: value })}>
                      <SelectTrigger className="text-sm"><SelectValue placeholder="اختر الحالة" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="coming_soon">قريبًا</SelectItem>
                        <SelectItem value="open">التسجيل مفتوح</SelectItem>
                        <SelectItem value="closed">التسجيل مغلق</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="schedule" className="mb-2 block text-sm">تفاصيل الجدول</Label>
                  <Input id="schedule" value={courseInfo.schedule} onChange={(e) => setCourseInfo({ ...courseInfo, schedule: e.target.value })} placeholder="مثال: كل يوم الساعة ٣ ظهراً بتوقيت غرينتش" className="text-sm" />
                </div>
                <div className="pt-4 lg:pt-6 border-t border-[#E2E8F0]">
                  <h4 className="heading-4 mb-4 flex items-center gap-2 text-base"><CreditCard className="w-5 h-5 text-[#7C3AED]" />إعدادات الدفع</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="price" className="mb-2 block flex items-center gap-2 text-sm"><DollarSign className="w-4 h-4" />السعر</Label>
                      <Input id="price" value={courseInfo.price} onChange={(e) => setCourseInfo({ ...courseInfo, price: e.target.value })} placeholder="499" className="text-sm" />
                    </div>
                    <div>
                      <Label htmlFor="currency" className="mb-2 block text-sm">العملة</Label>
                      <Select value={courseInfo.currency || "USD"} onValueChange={(value) => setCourseInfo({ ...courseInfo, currency: value })}>
                        <SelectTrigger className="text-sm"><SelectValue placeholder="اختر العملة" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="USD">دولار ($)</SelectItem>
                          <SelectItem value="EUR">يورو (€)</SelectItem>
                          <SelectItem value="GBP">جنيه (£)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                <Button type="submit" className="bg-gradient-to-r from-[#7C3AED] to-[#EC4899] hover:from-[#6D28D9] hover:to-[#DB2777] w-full" disabled={savingCourse}>
                  {savingCourse ? (<><RefreshCw className="w-4 h-4 ml-2 animate-spin" />جاري الحفظ...</>) : (<><Save className="w-4 h-4 ml-2" />حفظ معلومات الدورة</>)}
                </Button>
              </form>
            </div>
          </div>
        )}

        {activeTab === "users" && (
          <div data-testid="admin-users-section">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 lg:mb-8">
              <div>
                <h1 className="heading-2 mb-2 text-xl lg:text-3xl">المستخدمون المسجلون</h1>
                <p className="body-text text-sm lg:text-base">جميع المستخدمين وحالة الدفع.</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <Button variant="outline" onClick={fetchUsers} className="border-[#E2E8F0] w-full sm:w-auto">
                  <RefreshCw className="w-4 h-4 ml-2" />تحديث
                </Button>
                <Button onClick={openAddUser} className="bg-gradient-to-r from-[#7C3AED] to-[#EC4899] text-white shadow-lg shadow-purple-500/25 w-full sm:w-auto" data-testid="add-user-button">
                  <UserPlus className="w-4 h-4 ml-2" />إضافة مستخدم
                </Button>
              </div>
            </div>

            {/* Stats row */}
            {(() => {
              const totalUsers = users.length;
              const paidUsers = users.filter(u => u.paid).length;
              const unpaidUsers = totalUsers - paidUsers;
              const totalRevenue = users.reduce((sum, u) => sum + (u.paid ? (u.amount_paid || 0) : 0), 0);
              return (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 mb-6">
                  <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
                    <div className="flex items-center gap-2 text-[#8B92B3] text-xs mb-1"><Users className="w-4 h-4" /><span>إجمالي</span></div>
                    <p className="text-2xl font-black text-[#1A1D3D]" data-testid="users-stat-total">{totalUsers}</p>
                  </div>
                  <div className="bg-gradient-to-br from-[#ECFDF5] to-[#DCFCE7] border border-[#10B981]/30 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-[#059669] text-xs mb-1"><CheckCircle className="w-4 h-4" /><span>مدفوع</span></div>
                    <p className="text-2xl font-black text-[#059669]" data-testid="users-stat-paid">{paidUsers}</p>
                  </div>
                  <div className="bg-gradient-to-br from-[#FEF2F2] to-[#FEE2E2] border border-[#EF4444]/30 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-[#DC2626] text-xs mb-1"><XCircle className="w-4 h-4" /><span>لم يدفع</span></div>
                    <p className="text-2xl font-black text-[#DC2626]" data-testid="users-stat-unpaid">{unpaidUsers}</p>
                  </div>
                  <div className="bg-gradient-to-br from-[#F0F4FF] to-[#FCE7F3] border border-[#7C3AED]/30 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-[#7C3AED] text-xs mb-1"><DollarSign className="w-4 h-4" /><span>الإيرادات</span></div>
                    <p className="text-2xl font-black text-[#7C3AED]" data-testid="users-stat-revenue">${totalRevenue.toLocaleString()}</p>
                  </div>
                </div>
              );
            })()}

            {/* Filters row */}
            <div className="flex flex-col lg:flex-row gap-3 mb-4">
              <div className="inline-flex items-center bg-white border border-[#E2E8F0] rounded-xl p-1 gap-1 overflow-x-auto self-start">
                {[
                  { id: "all", label: "الكل" },
                  { id: "paid", label: "مدفوع" },
                  { id: "unpaid", label: "لم يدفع" },
                ].map(f => (
                  <button
                    key={f.id}
                    onClick={() => setUserFilter(f.id)}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all ${
                      userFilter === f.id
                        ? "bg-gradient-to-r from-[#7C3AED] to-[#EC4899] text-white shadow-md shadow-purple-500/25"
                        : "text-[#4A5078] hover:text-[#7C3AED] hover:bg-[#F0F4FF]"
                    }`}
                    data-testid={`users-filter-${f.id}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <div className="relative flex-1 max-w-md">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8B92B3]" />
                <Input
                  placeholder="ابحث بالاسم أو البريد أو الجوال أو الدولة..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="pr-10 bg-white border-[#E2E8F0]"
                  data-testid="users-search-input"
                />
              </div>
              <Button
                variant="outline"
                onClick={() => setAdvFiltersOpen(o => !o)}
                className={`border-[#E2E8F0] ${advFiltersOpen ? "bg-[#F0F4FF] text-[#7C3AED] border-[#7C3AED]/30" : ""}`}
                data-testid="adv-filters-toggle"
              >
                <Filter className="w-4 h-4 ml-2" />
                فلاتر متقدمة
                {(() => {
                  const active = Object.entries(advFilters).filter(([k, v]) => {
                    if (k === "sortBy" || k === "sortDir") return false;
                    return v !== "all" && v !== "";
                  }).length;
                  return active > 0 ? <span className="mr-2 px-1.5 py-0.5 rounded-full bg-[#7C3AED] text-white text-[10px]">{active}</span> : null;
                })()}
                <ChevronDown className={`w-4 h-4 mr-1 transition-transform ${advFiltersOpen ? "rotate-180" : ""}`} />
              </Button>
            </div>

            {/* Advanced Filters Panel */}
            {advFiltersOpen && (
              <div className="card-dashboard p-4 lg:p-5 mb-4 border-2 border-[#7C3AED]/15" data-testid="adv-filters-panel">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-[#1A1D3D] text-sm flex items-center gap-2">
                    <Filter className="w-4 h-4 text-[#7C3AED]" /> فلترة متقدمة
                  </h3>
                  <Button variant="ghost" size="sm" onClick={resetAdvFilters} className="text-[#EC4899] hover:bg-[#FEF3F2]" data-testid="adv-filters-reset">
                    <X className="w-4 h-4 ml-1" /> مسح الكل
                  </Button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {/* Provider */}
                  <div>
                    <Label className="text-xs">وسيلة الدفع</Label>
                    <Select value={advFilters.provider} onValueChange={(v) => setAdvFilters(s => ({...s, provider: v}))}>
                      <SelectTrigger className="text-sm" data-testid="adv-provider"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">الكل</SelectItem>
                        <SelectItem value="stripe">Stripe</SelectItem>
                        <SelectItem value="paypal">PayPal</SelectItem>
                        <SelectItem value="manual">يدوي</SelectItem>
                        <SelectItem value="none">بدون دفعة</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Manual method */}
                  <div>
                    <Label className="text-xs">طريقة الدفع اليدوي</Label>
                    <Select value={advFilters.manualMethod} onValueChange={(v) => setAdvFilters(s => ({...s, manualMethod: v}))}>
                      <SelectTrigger className="text-sm" data-testid="adv-manual-method"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">الكل</SelectItem>
                        <SelectItem value="cash">نقدًا</SelectItem>
                        <SelectItem value="bank_transfer">تحويل بنكي</SelectItem>
                        <SelectItem value="stripe_external">Stripe خارجي</SelectItem>
                        <SelectItem value="paypal_external">PayPal خارجي</SelectItem>
                        <SelectItem value="other">أخرى</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Country */}
                  <div>
                    <Label className="text-xs">الدولة</Label>
                    <Select value={advFilters.country} onValueChange={(v) => setAdvFilters(s => ({...s, country: v}))}>
                      <SelectTrigger className="text-sm" data-testid="adv-country"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">الكل</SelectItem>
                        {[...new Set(users.map(u => u.country).filter(Boolean))].sort().map(c => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Paid date range */}
                  <div>
                    <Label className="text-xs">تاريخ الدفع — من</Label>
                    <Input type="date" value={advFilters.paidFrom} onChange={(e) => setAdvFilters(s => ({...s, paidFrom: e.target.value}))} className="text-sm" dir="ltr" data-testid="adv-paid-from" />
                  </div>
                  <div>
                    <Label className="text-xs">تاريخ الدفع — إلى</Label>
                    <Input type="date" value={advFilters.paidTo} onChange={(e) => setAdvFilters(s => ({...s, paidTo: e.target.value}))} className="text-sm" dir="ltr" data-testid="adv-paid-to" />
                  </div>

                  {/* Created date range */}
                  <div>
                    <Label className="text-xs">تاريخ التسجيل — من</Label>
                    <Input type="date" value={advFilters.createdFrom} onChange={(e) => setAdvFilters(s => ({...s, createdFrom: e.target.value}))} className="text-sm" dir="ltr" data-testid="adv-created-from" />
                  </div>
                  <div>
                    <Label className="text-xs">تاريخ التسجيل — إلى</Label>
                    <Input type="date" value={advFilters.createdTo} onChange={(e) => setAdvFilters(s => ({...s, createdTo: e.target.value}))} className="text-sm" dir="ltr" data-testid="adv-created-to" />
                  </div>

                  {/* Amount range */}
                  <div>
                    <Label className="text-xs">المبلغ — أقل من</Label>
                    <Input type="number" placeholder="0" value={advFilters.amountMin} onChange={(e) => setAdvFilters(s => ({...s, amountMin: e.target.value}))} className="text-sm" dir="ltr" data-testid="adv-amount-min" />
                  </div>
                  <div>
                    <Label className="text-xs">المبلغ — أكثر من</Label>
                    <Input type="number" placeholder="∞" value={advFilters.amountMax} onChange={(e) => setAdvFilters(s => ({...s, amountMax: e.target.value}))} className="text-sm" dir="ltr" data-testid="adv-amount-max" />
                  </div>

                  {/* Has phone */}
                  <div>
                    <Label className="text-xs">رقم الجوال</Label>
                    <Select value={advFilters.hasPhone} onValueChange={(v) => setAdvFilters(s => ({...s, hasPhone: v}))}>
                      <SelectTrigger className="text-sm" data-testid="adv-phone"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">الكل</SelectItem>
                        <SelectItem value="yes">يملك جوال</SelectItem>
                        <SelectItem value="no">بدون جوال</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Role */}
                  <div>
                    <Label className="text-xs">الدور</Label>
                    <Select value={advFilters.role} onValueChange={(v) => setAdvFilters(s => ({...s, role: v}))}>
                      <SelectTrigger className="text-sm" data-testid="adv-role"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">الكل</SelectItem>
                        <SelectItem value="user">مستخدم</SelectItem>
                        <SelectItem value="admin">مسؤول</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Enrolled */}
                  <div>
                    <Label className="text-xs">حالة التسجيل في الدورة</Label>
                    <Select value={advFilters.enrolled} onValueChange={(v) => setAdvFilters(s => ({...s, enrolled: v}))}>
                      <SelectTrigger className="text-sm" data-testid="adv-enrolled"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">الكل</SelectItem>
                        <SelectItem value="yes">مسجَّل</SelectItem>
                        <SelectItem value="no">غير مسجَّل</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Sort */}
                  <div className="sm:col-span-2 lg:col-span-3 grid grid-cols-2 gap-3 mt-2 pt-3 border-t border-[#E2E8F0]">
                    <div>
                      <Label className="text-xs">ترتيب حسب</Label>
                      <Select value={advFilters.sortBy} onValueChange={(v) => setAdvFilters(s => ({...s, sortBy: v}))}>
                        <SelectTrigger className="text-sm" data-testid="adv-sort-by"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="created_at">تاريخ التسجيل</SelectItem>
                          <SelectItem value="paid_at">تاريخ الدفع</SelectItem>
                          <SelectItem value="amount_paid">المبلغ</SelectItem>
                          <SelectItem value="name">الاسم</SelectItem>
                          <SelectItem value="email">البريد</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">الاتجاه</Label>
                      <Select value={advFilters.sortDir} onValueChange={(v) => setAdvFilters(s => ({...s, sortDir: v}))}>
                        <SelectTrigger className="text-sm" data-testid="adv-sort-dir"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="desc">تنازلي (الأحدث أولاً)</SelectItem>
                          <SelectItem value="asc">تصاعدي (الأقدم أولاً)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Result count */}
            <div className="flex items-center justify-between mb-3 text-xs text-[#8B92B3]">
              <span>
                <strong className="text-[#1A1D3D]">{users.filter(u => {
                  // mirror the same filter for count display
                  const q = userSearch.trim().toLowerCase();
                  if (userFilter === "paid" && !u.paid) return false;
                  if (userFilter === "unpaid" && u.paid) return false;
                  if (q && !((u.name||"").toLowerCase().includes(q) || (u.email||"").toLowerCase().includes(q) || (u.phone||"").toLowerCase().includes(q) || (u.country||"").toLowerCase().includes(q))) return false;
                  if (advFilters.provider !== "all") {
                    if (advFilters.provider === "none") { if (u.payment_provider) return false; }
                    else if (u.payment_provider !== advFilters.provider) return false;
                  }
                  if (advFilters.manualMethod !== "all" && u.payment_method !== advFilters.manualMethod) return false;
                  if (advFilters.country !== "all" && (u.country || "—") !== advFilters.country) return false;
                  if (advFilters.hasPhone === "yes" && !u.phone) return false;
                  if (advFilters.hasPhone === "no" && u.phone) return false;
                  if (advFilters.role !== "all" && (u.role || "user") !== advFilters.role) return false;
                  if (advFilters.enrolled === "yes" && !u.enrolled) return false;
                  if (advFilters.enrolled === "no" && u.enrolled) return false;
                  return true;
                }).length}</strong> من أصل <strong className="text-[#1A1D3D]">{users.length}</strong> مستخدم
              </span>
            </div>

            <div className="card-dashboard overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead>المستخدم</TableHead>
                    <TableHead className="hidden sm:table-cell">البريد الإلكتروني</TableHead>
                    <TableHead>حالة الدفع</TableHead>
                    <TableHead className="hidden md:table-cell">المصدر</TableHead>
                    <TableHead className="hidden md:table-cell">المبلغ</TableHead>
                    <TableHead className="hidden lg:table-cell">تاريخ الدفع</TableHead>
                    <TableHead className="hidden lg:table-cell">المتبقي من الوصول</TableHead>
                    <TableHead className="hidden lg:table-cell">الجوال</TableHead>
                    <TableHead className="hidden xl:table-cell">الدولة</TableHead>
                    <TableHead className="hidden lg:table-cell">تاريخ التسجيل</TableHead>
                    <TableHead className="text-left">إجراءات</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(() => {
                    const q = userSearch.trim().toLowerCase();
                    const inRange = (val, fromDate, toDate) => {
                      if (!val) return !fromDate && !toDate;
                      const t = new Date(val).getTime();
                      if (fromDate && t < new Date(fromDate).getTime()) return false;
                      if (toDate && t > new Date(toDate).getTime() + 86399999) return false; // include end day
                      return true;
                    };
                    let filtered = users.filter(u => {
                      // status tabs
                      if (userFilter === "paid" && !u.paid) return false;
                      if (userFilter === "unpaid" && u.paid) return false;
                      // search
                      if (q && !((u.name || "").toLowerCase().includes(q) ||
                                 (u.email || "").toLowerCase().includes(q) ||
                                 (u.phone || "").toLowerCase().includes(q) ||
                                 (u.country || "").toLowerCase().includes(q))) return false;
                      // provider
                      if (advFilters.provider !== "all") {
                        if (advFilters.provider === "none") {
                          if (u.payment_provider) return false;
                        } else if (u.payment_provider !== advFilters.provider) {
                          return false;
                        }
                      }
                      // manual method (only meaningful when provider=manual)
                      if (advFilters.manualMethod !== "all" && u.payment_method !== advFilters.manualMethod) return false;
                      // country
                      if (advFilters.country !== "all" && (u.country || "—") !== advFilters.country) return false;
                      // paid date range
                      if (advFilters.paidFrom || advFilters.paidTo) {
                        if (!u.paid_at || !inRange(u.paid_at, advFilters.paidFrom, advFilters.paidTo)) return false;
                      }
                      // created date range
                      if (advFilters.createdFrom || advFilters.createdTo) {
                        if (!inRange(u.created_at, advFilters.createdFrom, advFilters.createdTo)) return false;
                      }
                      // amount range
                      const amt = Number(u.amount_paid) || 0;
                      if (advFilters.amountMin !== "" && amt < Number(advFilters.amountMin)) return false;
                      if (advFilters.amountMax !== "" && amt > Number(advFilters.amountMax)) return false;
                      // has phone
                      if (advFilters.hasPhone === "yes" && !u.phone) return false;
                      if (advFilters.hasPhone === "no" && u.phone) return false;
                      // role
                      if (advFilters.role !== "all" && (u.role || "user") !== advFilters.role) return false;
                      // enrolled
                      if (advFilters.enrolled === "yes" && !u.enrolled) return false;
                      if (advFilters.enrolled === "no" && u.enrolled) return false;
                      return true;
                    });
                    // sort
                    const dir = advFilters.sortDir === "asc" ? 1 : -1;
                    const key = advFilters.sortBy;
                    filtered = [...filtered].sort((a, b) => {
                      const av = a[key] ?? "";
                      const bv = b[key] ?? "";
                      if (key === "amount_paid") return ((Number(av) || 0) - (Number(bv) || 0)) * dir;
                      if (key === "paid_at" || key === "created_at") {
                        return ((new Date(av).getTime() || 0) - (new Date(bv).getTime() || 0)) * dir;
                      }
                      return String(av).localeCompare(String(bv), "ar") * dir;
                    });
                    // expose to outer scope via a side effect (read-only — for the count display)
                    window.__filteredUsersCount = filtered.length;
                    if (filtered.length === 0) {
                      return (
                        <TableRow>
                          <TableCell colSpan={10} className="text-center py-12">
                            <Users className="w-12 h-12 text-[#D1D5DB] mx-auto mb-4" />
                            <p className="text-[#8B92B3]">لا يوجد مستخدمون يطابقون الفلتر الحالي.</p>
                          </TableCell>
                        </TableRow>
                      );
                    }
                    const providerLabel = (p, m) => {
                      if (p === "stripe") return { text: "Stripe", color: "text-[#7C3AED] bg-[#F0F4FF] border-[#7C3AED]/30", icon: CreditCard };
                      if (p === "paypal") return { text: "PayPal", color: "text-[#003087] bg-[#FEF3C7] border-[#FBBF24]/40", icon: CreditCard };
                      if (p === "manual") {
                        const map = { cash: "نقدًا", bank_transfer: "تحويل بنكي", stripe_external: "Stripe خارجي", paypal_external: "PayPal خارجي", other: "أخرى" };
                        return { text: `يدوي • ${map[m] || m || "—"}`, color: "text-[#1A1D3D] bg-[#F4F4F5] border-[#E2E8F0]", icon: Edit };
                      }
                      return null;
                    };
                    return filtered.map((u) => {
                      const prov = providerLabel(u.payment_provider, u.payment_method);
                      return (
                        <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                          <TableCell>
                            <div className="flex items-center gap-2 lg:gap-3">
                              {u.picture ? <img src={u.picture} alt={u.name} className="w-8 h-8 rounded-full object-cover" /> : (
                                <div className="w-8 h-8 bg-gradient-to-br from-[#7C3AED] to-[#EC4899] rounded-full flex items-center justify-center"><User className="w-4 h-4 text-white" /></div>
                              )}
                              <div>
                                <span className="font-medium text-sm">{u.name}</span>
                                <p className="text-xs text-[#8B92B3] sm:hidden">{u.email}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="hidden sm:table-cell text-sm">{u.email}</TableCell>
                          <TableCell>
                            {u.paid ? (
                              <div className="flex flex-col gap-1">
                                <Badge className="bg-[#10B981]/10 text-[#059669] border border-[#10B981]/30 hover:bg-[#10B981]/20 w-fit">
                                  <CheckCircle className="w-3 h-3 ml-1" /> مدفوع
                                </Badge>
                                {prov && (
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold border w-fit ${prov.color}`}>
                                    <prov.icon className="w-3 h-3" />
                                    {prov.text}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <Badge className="bg-[#EF4444]/10 text-[#DC2626] border border-[#EF4444]/30 hover:bg-[#EF4444]/20">
                                <XCircle className="w-3 h-3 ml-1" /> لم يدفع
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            {prov ? (
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold border ${prov.color}`}>
                                <prov.icon className="w-3 h-3" />
                                {prov.text}
                              </span>
                            ) : <span className="text-[#8B92B3] text-xs">—</span>}
                          </TableCell>
                          <TableCell className="hidden md:table-cell text-sm font-semibold">
                            {u.paid ? `$${(u.amount_paid || 0).toLocaleString()}` : <span className="text-[#8B92B3]">—</span>}
                          </TableCell>
                          <TableCell className="hidden lg:table-cell text-sm text-[#4A5078]">
                            {u.paid_at ? (
                              <div className="flex flex-col leading-tight">
                                <span>{new Date(u.paid_at).toLocaleDateString('ar')}</span>
                                <span className="text-xs text-[#8B92B3]" dir="ltr">
                                  {new Date(u.paid_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>
                            ) : <span className="text-[#8B92B3]">—</span>}
                          </TableCell>
                          <TableCell className="hidden lg:table-cell text-sm">
                            {(() => {
                              if (!u.paid_at) return <span className="text-[#8B92B3]">—</span>;
                              const paidMs = new Date(u.paid_at).getTime();
                              const expiryMs = paidMs + 365 * 24 * 60 * 60 * 1000;
                              const daysLeft = Math.ceil((expiryMs - Date.now()) / (24 * 60 * 60 * 1000));
                              const expired = daysLeft <= 0;
                              const expiringSoon = !expired && daysLeft <= 30;
                              const expiryLabel = new Date(expiryMs).toLocaleDateString('ar');
                              if (expired) {
                                return (
                                  <div className="flex flex-col leading-tight" data-testid={`access-remaining-${u.user_id}`}>
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#FEF2F2] text-[#DC2626] text-xs font-bold border border-[#FECACA] w-fit">منتهي</span>
                                    <span className="text-[10px] text-[#8B92B3] mt-0.5">انتهى في {expiryLabel}</span>
                                  </div>
                                );
                              }
                              const colorCls = expiringSoon
                                ? "bg-[#FEF3C7] text-[#B45309] border-[#FCD34D]"
                                : "bg-[#ECFDF5] text-[#059669] border-[#A7F3D0]";
                              return (
                                <div className="flex flex-col leading-tight" data-testid={`access-remaining-${u.user_id}`}>
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-bold border w-fit ${colorCls}`}>{daysLeft} يوم متبقّي</span>
                                  <span className="text-[10px] text-[#8B92B3] mt-0.5">حتى {expiryLabel}</span>
                                </div>
                              );
                            })()}
                          </TableCell>
                          <TableCell className="hidden lg:table-cell text-sm text-[#4A5078]" dir="ltr">
                            {u.phone || <span className="text-[#8B92B3]">—</span>}
                          </TableCell>
                          <TableCell className="hidden xl:table-cell text-sm text-[#4A5078]">
                            {u.country || <span className="text-[#8B92B3]">—</span>}
                          </TableCell>
                          <TableCell className="hidden lg:table-cell text-sm">
                            {u.created_at ? (
                              <div className="flex flex-col leading-tight">
                                <span>{new Date(u.created_at).toLocaleDateString('ar')}</span>
                                <span className="text-xs text-[#8B92B3]" dir="ltr">
                                  {new Date(u.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>
                            ) : '—'}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button size="icon" variant="ghost" onClick={() => openEditUser(u)} className="h-8 w-8 text-[#7C3AED] hover:bg-[#F0F4FF]" title="تعديل" data-testid={`edit-user-${u.user_id}`}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              {u.role !== "admin" && (
                                <Button size="icon" variant="ghost" onClick={() => setDeleteUserId(u.user_id)} className="h-8 w-8 text-[#EF4444] hover:bg-[#FEF2F2]" title="حذف" data-testid={`delete-user-${u.user_id}`}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    });
                  })()}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {activeTab === "messages" && (
          <div data-testid="admin-messages-section">
            <div className="mb-6 lg:mb-8">
              <h1 className="heading-2 mb-2 text-xl lg:text-3xl">إرسال روابط Zoom</h1>
              <p className="body-text text-sm lg:text-base">أرسل روابط جلسات Zoom للمستخدمين المسجلين.</p>
            </div>
            <div className="card-dashboard p-4 lg:p-6 max-w-2xl">
              <form onSubmit={handleSendMessage} className="space-y-4 lg:space-y-6">
                <div>
                  <Label htmlFor="recipient" className="mb-2 block text-sm">المستلم</Label>
                  <Select value={messageForm.recipient_id} onValueChange={(value) => setMessageForm({ ...messageForm, recipient_id: value })}>
                    <SelectTrigger className="text-sm" data-testid="zoom-recipient-select"><SelectValue placeholder="اختر المستلم" /></SelectTrigger>
                    <SelectContent>
                      {(() => {
                        const enrolled = users.filter(u => u.paid);
                        if (enrolled.length === 0) {
                          return (
                            <div className="px-3 py-4 text-center text-sm text-[#8B92B3]">
                              لا يوجد مستخدمون مدفوعون بعد.<br/>
                              <span className="text-xs">اذهب إلى "المستخدمون" لإضافة مستخدم مدفوع.</span>
                            </div>
                          );
                        }
                        return (
                          <>
                            <SelectItem value="all" data-testid="zoom-recipient-all">جميع المستخدمين المدفوعين ({enrolled.length})</SelectItem>
                            {enrolled.map((u) => (
                              <SelectItem key={u.user_id} value={u.user_id}>
                                {u.name} ({u.email})
                              </SelectItem>
                            ))}
                          </>
                        );
                      })()}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-[#8B92B3] mt-1.5">📌 روابط Zoom تُرسل فقط للمستخدمين المدفوعين.</p>
                </div>
                <div>
                  <Label htmlFor="subject" className="mb-2 block text-sm">عنوان الجلسة</Label>
                  <Input id="subject" value={messageForm.subject} onChange={(e) => setMessageForm({ ...messageForm, subject: e.target.value })} placeholder="مثال: رابط Zoom للجلسة الأولى — ٣٠ أيار" className="text-sm" />
                </div>
                <div>
                  <Label htmlFor="content" className="mb-2 block text-sm">رابط Zoom + التفاصيل</Label>
                  <Textarea id="content" value={messageForm.content} onChange={(e) => setMessageForm({ ...messageForm, content: e.target.value })} placeholder="https://us02web.zoom.us/j/...&#10;&#10;Meeting ID: 123 456 7890&#10;Passcode: ..." rows={6} className="resize-none text-sm" dir="ltr" />
                </div>
                <Button type="submit" className="bg-gradient-to-r from-[#7C3AED] to-[#EC4899] hover:from-[#6D28D9] hover:to-[#DB2777] w-full" disabled={sendingMessage}>
                  {sendingMessage ? (<><RefreshCw className="w-4 h-4 ml-2 animate-spin" />جاري الإرسال...</>) : (<><Send className="w-4 h-4 ml-2" />إرسال رابط Zoom</>)}
                </Button>
              </form>
            </div>
          </div>
        )}

        {activeTab === "sent" && (
          <div data-testid="admin-sent-section">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div>
                <h1 className="heading-2 mb-1 text-xl lg:text-3xl">روابط Zoom المُرسَلة</h1>
                <p className="body-text text-sm">{sentMessages.length} رابط مُرسَل</p>
              </div>
              <Button variant="outline" onClick={fetchSentMessages} className="border-[#E2E8F0] w-full sm:w-auto">
                <RefreshCw className="w-4 h-4 ml-2" />تحديث
              </Button>
            </div>
            <div className="card-dashboard p-3 lg:p-4 mb-4">
              <div className="relative">
                <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-[#8B92B3]" />
                <Input placeholder="بحث في الروابط المُرسَلة..." value={sentSearchQuery} onChange={(e) => setSentSearchQuery(e.target.value)} className="pr-10 text-sm" />
              </div>
            </div>
            <div className="card-dashboard">
              {getFilteredSentMessages().length > 0 ? (
                <div className="divide-y divide-[#E2E8F0]">
                  {getFilteredSentMessages().map((message) => (
                    <div key={message.message_id} onClick={() => setSelectedSentMessage(message)} className="p-4 cursor-pointer hover:bg-[#F0F4FF] transition-colors">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-[#F0F4FF] text-[#7C3AED] border border-[#7C3AED]/20">
                              <Video className="w-3 h-3" />Zoom
                            </span>
                            {message.read ? (
                              <span className="flex items-center gap-1 text-xs text-green-600"><CheckCircle className="w-3 h-3" />مقروء</span>
                            ) : (
                              <span className="flex items-center gap-1 text-xs text-[#8B92B3]"><XCircle className="w-3 h-3" />غير مقروء</span>
                            )}
                          </div>
                          <p className="font-medium text-sm text-[#1A1D3D] truncate">{message.subject}</p>
                          <p className="text-xs text-[#8B92B3] truncate">إلى: {getRecipientName(message.recipient_id)}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-[#8B92B3] whitespace-nowrap">{formatDate(message.created_at)}</span>
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setMessageToDelete(message.message_id); setDeleteMessageDialogOpen(true); }} className="text-red-500 hover:text-red-600 hover:bg-red-50 p-1 h-auto">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-12 text-center">
                  <Video className="w-12 h-12 text-[#D1D5DB] mx-auto mb-4" />
                  <h3 className="heading-4 mb-2">لا توجد روابط مُرسَلة بعد</h3>
                  <p className="text-small mb-6">أرسل رابط Zoom الأول للمستخدمين.</p>
                  <Button onClick={() => handleTabChange("messages")} className="bg-gradient-to-r from-[#7C3AED] to-[#EC4899]">
                    <Video className="w-4 h-4 ml-2" />إرسال رابط Zoom
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Backup / Restore Tab */}
        {activeTab === "backup" && (
          <div data-testid="admin-backup-section" className="max-w-5xl mx-auto">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div>
                <p className="overline mb-1">إدارة قاعدة البيانات</p>
                <h1 className="heading-2 mb-1 text-xl lg:text-3xl flex items-center gap-2">
                  <Database className="w-7 h-7 text-[#7C3AED]" />
                  نسخ احتياطي / استيراد
                </h1>
                <p className="body-text text-sm">صدّر بيانات الموقع بالكامل بصيغة JSON أو استورد نسخة سابقة.</p>
              </div>
              <Button variant="outline" onClick={fetchBackupStats} className="border-[#E2E8F0]" data-testid="backup-refresh-btn">
                <RefreshCw className="w-4 h-4 ml-2" />تحديث
              </Button>
            </div>

            {/* Stats Grid */}
            {backupStats && (
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
                {[
                  { label: "المستخدمون", val: backupStats.summary.total_users, icon: Users, color: "from-[#7C3AED] to-[#EC4899]" },
                  { label: "المدفوعون", val: backupStats.summary.paid_users, icon: CheckCircle, color: "from-emerald-500 to-teal-500" },
                  { label: "الدفعات", val: backupStats.summary.total_payments, icon: CreditCard, color: "from-blue-500 to-cyan-500" },
                  { label: "الإيرادات (USD)", val: `$${backupStats.summary.total_revenue_usd}`, icon: DollarSign, color: "from-amber-500 to-orange-500" },
                  { label: "الرسائل", val: backupStats.summary.total_messages, icon: Mail, color: "from-pink-500 to-rose-500" },
                ].map((s, i) => (
                  <div key={i} className="card-dashboard p-4">
                    <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${s.color} flex items-center justify-center mb-2 shadow`}>
                      <s.icon className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-xs text-[#8B92B3]">{s.label}</p>
                    <p className="text-lg lg:text-xl font-bold text-[#1A1D3D]">{s.val}</p>
                  </div>
                ))}
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Export Card */}
              <div className="card-dashboard p-5 lg:p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#7C3AED] to-[#EC4899] flex items-center justify-center">
                    <Download className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#1A1D3D]">تصدير البيانات</h3>
                    <p className="text-xs text-[#8B92B3]">نزّل النسخ الاحتياطية بصيغة JSON</p>
                  </div>
                </div>

                <Button
                  onClick={() => exportBackup()}
                  disabled={backupLoading}
                  className="w-full bg-gradient-to-r from-[#7C3AED] to-[#EC4899] hover:from-[#6D28D9] hover:to-[#DB2777] text-white py-5 rounded-xl shadow-lg shadow-purple-500/25 mb-3"
                  data-testid="backup-export-all-btn"
                >
                  <HardDrive className="w-4 h-4 ml-2" />
                  تصدير قاعدة البيانات بالكامل
                </Button>

                <p className="text-xs text-[#8B92B3] mb-2 mt-4">أو صدّر مجموعة معيّنة فقط:</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { id: "users", label: "المستخدمون" },
                    { id: "payment_transactions", label: "الدفعات" },
                    { id: "messages", label: "روابط Zoom" },
                    { id: "course_info", label: "معلومات الدورة" },
                  ].map(c => (
                    <Button
                      key={c.id}
                      variant="outline"
                      onClick={() => exportBackup(c.id)}
                      disabled={backupLoading}
                      className="border-[#E2E8F0] text-[#4A5078] hover:bg-[#F0F4FF] text-xs lg:text-sm"
                      data-testid={`backup-export-${c.id}`}
                    >
                      <Download className="w-3.5 h-3.5 ml-1" />{c.label}
                      <span className="text-[#8B92B3] mr-1">({backupStats?.collections?.[c.id] ?? "—"})</span>
                    </Button>
                  ))}
                </div>
              </div>

              {/* Import Card */}
              <div className="card-dashboard p-5 lg:p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                    <Upload className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#1A1D3D]">استيراد البيانات</h3>
                    <p className="text-xs text-[#8B92B3]">ارفع ملف JSON من تصدير سابق</p>
                  </div>
                </div>

                <Label htmlFor="backup-import-file" className="text-xs text-[#4A5078]">ملف النسخة الاحتياطية (JSON)</Label>
                <Input
                  id="backup-import-file"
                  type="file"
                  accept="application/json,.json"
                  onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportReport(null); }}
                  className="mt-1 text-sm"
                  data-testid="backup-import-file"
                />
                {importFile && (
                  <p className="text-xs text-[#4A5078] mt-2">
                    📄 <strong>{importFile.name}</strong> ({(importFile.size / 1024).toFixed(1)} KB)
                  </p>
                )}

                <div className="mt-4">
                  <Label className="text-xs text-[#4A5078]">وضع الاستيراد</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    <button
                      type="button"
                      onClick={() => setImportMode("merge")}
                      className={`p-3 rounded-lg border-2 text-right transition-all ${importMode === "merge" ? "border-[#7C3AED] bg-[#F0F4FF]" : "border-[#E2E8F0] hover:border-[#7C3AED]/40"}`}
                      data-testid="backup-mode-merge"
                    >
                      <div className="font-bold text-sm text-[#1A1D3D]">دمج (آمن)</div>
                      <div className="text-xs text-[#8B92B3]">يُحدّث الموجود ويُضيف الجديد</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setImportMode("replace")}
                      className={`p-3 rounded-lg border-2 text-right transition-all ${importMode === "replace" ? "border-red-500 bg-red-50" : "border-[#E2E8F0] hover:border-red-300"}`}
                      data-testid="backup-mode-replace"
                    >
                      <div className="font-bold text-sm text-[#1A1D3D]">استبدال كامل ⚠️</div>
                      <div className="text-xs text-[#8B92B3]">يحذف كل البيانات الحالية</div>
                    </button>
                  </div>
                </div>

                {importMode === "replace" && (
                  <div className="mt-3 p-3 rounded-lg bg-red-50 border border-red-200 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-red-700">
                      <strong>تحذير:</strong> سيتم <strong>حذف جميع البيانات الحالية</strong> في المجموعات المُستوردة وإحلال البيانات الجديدة محلها. لا يمكن التراجع.
                    </p>
                  </div>
                )}

                <Button
                  onClick={() => importMode === "replace" ? setConfirmReplaceOpen(true) : performImport()}
                  disabled={!importFile || importing}
                  className={`w-full mt-4 py-5 rounded-xl shadow-lg ${importMode === "replace" ? "bg-red-600 hover:bg-red-700 text-white" : "bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white"}`}
                  data-testid="backup-import-btn"
                >
                  <Upload className="w-4 h-4 ml-2" />
                  {importing ? "جارٍ الاستيراد..." : (importMode === "replace" ? "استبدال البيانات" : "دمج البيانات")}
                </Button>

                {importReport && (
                  <div className="mt-4 p-3 rounded-lg bg-[#F0FDF4] border border-emerald-200">
                    <p className="text-xs font-bold text-emerald-800 mb-1">تقرير الاستيراد</p>
                    <pre className="text-[10px] text-emerald-700 overflow-auto max-h-40">{JSON.stringify(importReport, null, 2)}</pre>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 p-4 rounded-xl bg-[#FEF3C7]/50 border border-amber-200 text-xs text-amber-900 leading-relaxed">
              <strong>📌 نصائح:</strong>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>صدّر نسخة احتياطية بانتظام (أسبوعيًا على الأقل) قبل أي تعديلات كبيرة.</li>
                <li>وضع <strong>الدمج</strong> هو الأكثر أمانًا — يُحدّث السجلات الموجودة بناءً على معرّفها الفريد ويُضيف الجديد.</li>
                <li>وضع <strong>الاستبدال</strong> يحذف كل شيء — استخدمه فقط لاستعادة كاملة من نسخة سابقة موثوقة.</li>
                <li>الجلسات الإدارية وأنشطة Stripe الفعلية لا تُصدَّر/تُستورَد لأسباب أمنية.</li>
              </ul>
            </div>
          </div>
        )}

        {/* Reviews Tab */}
        {activeTab === "reviews" && (
          <AdminReviewsTab />
        )}

      </main>

      <AlertDialog open={confirmReplaceOpen} onOpenChange={setConfirmReplaceOpen}>
        <AlertDialogContent className="max-w-[90vw] sm:max-w-lg" dir="rtl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              تأكيد الاستبدال الكامل
            </AlertDialogTitle>
            <AlertDialogDescription>
              سيتم حذف جميع البيانات الحالية في المجموعات الموجودة في الملف <strong>{importFile?.name}</strong> واستبدالها بالكامل.
              <br/><br/>
              <strong className="text-red-600">لا يمكن التراجع عن هذا الإجراء.</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row-reverse gap-2">
            <AlertDialogAction onClick={performImport} className="bg-red-600 hover:bg-red-700" data-testid="backup-confirm-replace">
              نعم، استبدل
            </AlertDialogAction>
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteMessageDialogOpen} onOpenChange={setDeleteMessageDialogOpen}>
        <AlertDialogContent className="max-w-[90vw] sm:max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>حذف الرسالة</AlertDialogTitle>
            <AlertDialogDescription>هل أنت متأكد من حذف هذه الرسالة؟ سيتم إزالتها أيضًا من بريد المستلم.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row-reverse gap-2">
            <AlertDialogAction onClick={handleDeleteSentMessage} className="bg-red-500 hover:bg-red-600">حذف</AlertDialogAction>
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add / Edit User Dialog */}
      <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" dir="rtl" data-testid="user-form-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              {editUserId ? <Edit className="w-5 h-5 text-[#7C3AED]" /> : <UserPlus className="w-5 h-5 text-[#7C3AED]" />}
              {editUserId ? "تعديل مستخدم" : "إضافة مستخدم جديد"}
            </DialogTitle>
            <DialogDescription>
              {editUserId ? "حدّث بيانات المستخدم. الدفع لا يُعدَّل من هنا." : "أنشئ مستخدمًا جديدًا يدويًا (مدفوع خارجيًا أو مجرد إضافة)."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="uf-name" className="text-sm">الاسم الكامل *</Label>
                <Input id="uf-name" value={userForm.name} onChange={(e) => setUserForm(s => ({...s, name: e.target.value}))} className="mt-1" data-testid="uf-name" />
              </div>
              <div>
                <Label htmlFor="uf-email" className="text-sm">البريد الإلكتروني *</Label>
                <Input id="uf-email" type="email" dir="ltr" value={userForm.email} onChange={(e) => setUserForm(s => ({...s, email: e.target.value}))} className="mt-1" data-testid="uf-email" />
              </div>
              <div>
                <Label htmlFor="uf-phone" className="text-sm">الجوال / واتساب</Label>
                <Input id="uf-phone" dir="ltr" placeholder="+963..." value={userForm.phone} onChange={(e) => setUserForm(s => ({...s, phone: e.target.value}))} className="mt-1" data-testid="uf-phone" />
              </div>
              <div>
                <Label htmlFor="uf-country" className="text-sm">الدولة</Label>
                <Input id="uf-country" value={userForm.country} onChange={(e) => setUserForm(s => ({...s, country: e.target.value}))} className="mt-1" data-testid="uf-country" />
              </div>
            </div>
            <div>
              <Label htmlFor="uf-notes" className="text-sm">ملاحظات داخلية</Label>
              <Textarea id="uf-notes" rows={2} value={userForm.notes} onChange={(e) => setUserForm(s => ({...s, notes: e.target.value}))} className="mt-1" data-testid="uf-notes" />
            </div>

            {!editUserId ? (
              <>
                <div className="border-t border-[#E2E8F0] pt-4">
                  <Label className="text-sm font-bold mb-2 block">نوع الإضافة *</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { id: "paid", label: "مدفوع", icon: CheckCircle, color: "from-[#10B981] to-[#059669]" },
                      { id: "none", label: "بدون دفع", icon: User, color: "from-[#9CA3AF] to-[#6B7280]" },
                    ].map(t => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setUserForm(s => ({...s, payment_type: t.id, amount: t.id === "paid" ? "499" : ""}))}
                        className={`flex flex-col items-center gap-1.5 px-3 py-3 rounded-xl border-2 text-sm font-semibold transition-all ${
                          userForm.payment_type === t.id
                            ? `bg-gradient-to-br ${t.color} text-white border-transparent shadow-md`
                            : "bg-white text-[#4A5078] border-[#E2E8F0] hover:border-[#7C3AED]/40"
                        }`}
                        data-testid={`uf-type-${t.id}`}
                      >
                        <t.icon className="w-5 h-5" />
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                {userForm.payment_type === "paid" && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="uf-amount" className="text-sm">المبلغ (USD)</Label>
                      <Input id="uf-amount" type="number" min="0" step="0.01" dir="ltr" value={userForm.amount} onChange={(e) => setUserForm(s => ({...s, amount: e.target.value}))} className="mt-1" data-testid="uf-amount" />
                    </div>
                    <div>
                      <Label htmlFor="uf-method" className="text-sm">طريقة الدفع</Label>
                      <Select value={userForm.method} onValueChange={(v) => setUserForm(s => ({...s, method: v}))}>
                        <SelectTrigger id="uf-method" className="mt-1" data-testid="uf-method"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cash">نقدًا</SelectItem>
                          <SelectItem value="bank_transfer">تحويل بنكي</SelectItem>
                          <SelectItem value="stripe_external">Stripe (خارج الموقع)</SelectItem>
                          <SelectItem value="paypal_external">PayPal (خارج الموقع)</SelectItem>
                          <SelectItem value="other">أخرى</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="uf-reference" className="text-sm">رقم المرجع / الإيصال</Label>
                      <Input id="uf-reference" dir="ltr" value={userForm.reference} onChange={(e) => setUserForm(s => ({...s, reference: e.target.value}))} className="mt-1" data-testid="uf-reference" />
                    </div>
                    <div>
                      <Label htmlFor="uf-paid-at" className="text-sm">تاريخ الدفع</Label>
                      <Input id="uf-paid-at" type="date" dir="ltr" value={userForm.paid_at} onChange={(e) => setUserForm(s => ({...s, paid_at: e.target.value}))} className="mt-1" data-testid="uf-paid-at" />
                    </div>
                  </div>
                )}

                {userForm.payment_type === "paid" && (
                  <div className="flex items-center justify-between bg-[#F0F4FF] border border-[#E2E8F0] rounded-xl px-4 py-3">
                    <Label htmlFor="uf-welcome" className="text-sm font-semibold cursor-pointer">إرسال رسالة ترحيب وروابط الجلسات</Label>
                    <Switch id="uf-welcome" checked={userForm.send_welcome} onCheckedChange={(v) => setUserForm(s => ({...s, send_welcome: v}))} data-testid="uf-welcome" />
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="border-t border-[#E2E8F0] pt-4">
                  <Label className="text-sm font-bold mb-1 block">حالة الدفع</Label>
                  <p className="text-xs text-[#8B92B3] mb-3">يمكنك تغيير الحالة لأي مستخدم. سجلات Stripe الفعلية محفوظة في الأرشيف.</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { id: "paid", label: "مدفوع", icon: CheckCircle, color: "from-[#10B981] to-[#059669]" },
                      { id: "none", label: "غير مدفوع", icon: XCircle, color: "from-[#EF4444] to-[#DC2626]" },
                    ].map(t => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setUserForm(s => ({...s, payment_type: t.id, amount: t.id === "paid" ? (s.amount || "499") : s.amount}))}
                        className={`flex flex-col items-center gap-1.5 px-3 py-3 rounded-xl border-2 text-sm font-semibold transition-all ${
                          userForm.payment_type === t.id
                            ? `bg-gradient-to-br ${t.color} text-white border-transparent shadow-md`
                            : "bg-white text-[#4A5078] border-[#E2E8F0] hover:border-[#7C3AED]/40"
                        }`}
                        data-testid={`uf-type-${t.id}`}
                      >
                        <t.icon className="w-5 h-5" />
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                {userForm.payment_type !== userForm._initial_payment_type && userForm.payment_type === "paid" && (
                  <>
                    <div className="bg-[#FEF3C7] border border-[#F59E0B]/30 rounded-lg px-3 py-2 text-xs text-[#92400E] flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      ستُسجَّل دفعة جديدة عند الحفظ بالقيم أدناه.
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="uf-amount" className="text-sm">المبلغ (USD)</Label>
                        <Input id="uf-amount" type="number" min="0" step="0.01" dir="ltr" value={userForm.amount} onChange={(e) => setUserForm(s => ({...s, amount: e.target.value}))} className="mt-1" data-testid="uf-amount" />
                      </div>
                      <div>
                        <Label htmlFor="uf-method" className="text-sm">طريقة الدفع</Label>
                        <Select value={userForm.method} onValueChange={(v) => setUserForm(s => ({...s, method: v}))}>
                          <SelectTrigger id="uf-method" className="mt-1" data-testid="uf-method"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="cash">نقدًا</SelectItem>
                            <SelectItem value="bank_transfer">تحويل بنكي</SelectItem>
                            <SelectItem value="stripe_external">Stripe (خارج الموقع)</SelectItem>
                            <SelectItem value="other">أخرى</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="uf-reference" className="text-sm">رقم المرجع / الإيصال</Label>
                        <Input id="uf-reference" dir="ltr" value={userForm.reference} onChange={(e) => setUserForm(s => ({...s, reference: e.target.value}))} className="mt-1" data-testid="uf-reference" />
                      </div>
                      <div>
                        <Label htmlFor="uf-paid-at" className="text-sm">تاريخ الدفع</Label>
                        <Input id="uf-paid-at" type="date" dir="ltr" value={userForm.paid_at} onChange={(e) => setUserForm(s => ({...s, paid_at: e.target.value}))} className="mt-1" data-testid="uf-paid-at" />
                      </div>
                    </div>
                    <div className="flex items-center justify-between bg-[#F0F4FF] border border-[#E2E8F0] rounded-xl px-4 py-3">
                      <Label htmlFor="uf-welcome" className="text-sm font-semibold cursor-pointer">إرسال رسالة ترحيب</Label>
                      <Switch id="uf-welcome" checked={userForm.send_welcome} onCheckedChange={(v) => setUserForm(s => ({...s, send_welcome: v}))} data-testid="uf-welcome" />
                    </div>
                  </>
                )}

                {userForm.payment_type !== userForm._initial_payment_type && userForm.payment_type === "none" && (
                  <div className="bg-[#FEF2F2] border border-[#EF4444]/30 rounded-lg px-3 py-2 text-xs text-[#991B1B] flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    سيُلغى التسجيل وستُحوَّل الدفعات اليدوية إلى "ملغاة". سجلات Stripe لا تُحذف.
                  </div>
                )}
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddUser(false)}>إلغاء</Button>
            <Button onClick={submitUserForm} disabled={userFormSubmitting} className="bg-gradient-to-r from-[#7C3AED] to-[#EC4899] text-white" data-testid="uf-submit">
              {userFormSubmitting ? "جارٍ الحفظ..." : (editUserId ? "حفظ التغييرات" : "إضافة المستخدم")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete user confirmation */}
      <AlertDialog open={!!deleteUserId} onOpenChange={(o) => !o && setDeleteUserId(null)}>
        <AlertDialogContent dir="rtl">
          <AlertDialogHeader>
            <AlertDialogTitle>تأكيد حذف المستخدم</AlertDialogTitle>
            <AlertDialogDescription>
              سيتم حذف المستخدم وجميع رسائله ومدفوعاته نهائيًا. لا يمكن التراجع.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={confirmDeleteUser} className="bg-red-500 hover:bg-red-600" data-testid="confirm-delete-user">حذف نهائي</AlertDialogAction>
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ===== Admin Reviews Moderation =====
const AdminReviewsTab = () => {
  const API_LOCAL = `${window.location.origin}/api`;
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | approved | hidden

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_LOCAL}/admin/reviews`, { withCredentials: true });
      setReviews(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error("تعذّر تحميل التقييمات");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleApproved = async (r) => {
    try {
      await axios.patch(
        `${API_LOCAL}/admin/reviews/${r.review_id}`,
        { approved: !r.approved },
        { withCredentials: true }
      );
      toast.success(r.approved ? "تم إخفاء التقييم" : "تم إظهار التقييم");
      fetchReviews();
    } catch (e) {
      toast.error("تعذّر تحديث التقييم");
    }
  };

  const deleteReview = async (r) => {
    if (!window.confirm(`حذف تقييم ${r.user_name} نهائيًا؟`)) return;
    try {
      await axios.delete(`${API_LOCAL}/admin/reviews/${r.review_id}`, { withCredentials: true });
      toast.success("تم حذف التقييم");
      fetchReviews();
    } catch (e) {
      toast.error("تعذّر حذف التقييم");
    }
  };

  const filtered = reviews.filter((r) => {
    if (filter === "approved") return r.approved;
    if (filter === "hidden") return !r.approved;
    return true;
  });

  const total = reviews.length;
  const approvedCount = reviews.filter((r) => r.approved).length;
  const hiddenCount = total - approvedCount;
  const avg = total > 0
    ? (reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / total).toFixed(2)
    : "0.00";

  return (
    <div data-testid="admin-reviews-section" className="max-w-6xl mx-auto">
      <div className="mb-6">
        <p className="overline mb-1">إدارة التقييمات</p>
        <h1 className="heading-2 mb-1 text-xl lg:text-3xl flex items-center gap-2">
          <Star className="w-7 h-7 text-[#FBBF24]" fill="currentColor" />
          تقييمات المشاركين
        </h1>
        <p className="text-small">راجع، أخفِ أو احذف أيّ تقييم من المشاركين.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 shadow-sm">
          <p className="text-xs text-[#8B92B3]">المتوسط</p>
          <p className="text-2xl font-black text-[#1A1D3D]" dir="ltr">{avg}</p>
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 shadow-sm">
          <p className="text-xs text-[#8B92B3]">إجمالي التقييمات</p>
          <p className="text-2xl font-black text-[#1A1D3D]">{total}</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 shadow-sm">
          <p className="text-xs text-emerald-700">معتمدة</p>
          <p className="text-2xl font-black text-emerald-700">{approvedCount}</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 shadow-sm">
          <p className="text-xs text-red-700">مخفية</p>
          <p className="text-2xl font-black text-red-700">{hiddenCount}</p>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2 mb-4">
        {[
          { id: "all", label: `الكل (${total})` },
          { id: "approved", label: `معتمدة (${approvedCount})` },
          { id: "hidden", label: `مخفية (${hiddenCount})` },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setFilter(t.id)}
            className={`px-3 py-1.5 rounded-lg text-sm font-bold transition ${
              filter === t.id
                ? "bg-[#7C3AED] text-white"
                : "bg-white border border-[#E2E8F0] text-[#4A5078] hover:border-[#7C3AED]"
            }`}
            data-testid={`admin-reviews-filter-${t.id}`}
          >
            {t.label}
          </button>
        ))}
        <Button
          variant="outline"
          size="sm"
          onClick={fetchReviews}
          className="mr-auto"
          data-testid="admin-reviews-refresh"
        >
          <RefreshCw className="w-4 h-4 ml-1" />
          تحديث
        </Button>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-10">
          <RefreshCw className="w-8 h-8 text-[#7C3AED] animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-10 text-center text-[#8B92B3]">
          لا توجد تقييمات في هذه الفئة.
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => (
            <div
              key={r.review_id}
              className={`bg-white border rounded-2xl p-4 md:p-5 shadow-sm transition ${
                r.approved ? "border-[#E2E8F0]" : "border-red-200 bg-red-50/40"
              }`}
              data-testid={`admin-review-${r.review_id}`}
            >
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <p className="font-bold text-[#1A1D3D]">{r.user_name}</p>
                    <span className="inline-flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Star
                          key={i}
                          className={`w-4 h-4 ${i <= r.rating ? "text-[#FBBF24]" : "text-[#E5E7EB]"}`}
                          fill="currentColor"
                        />
                      ))}
                    </span>
                    <span className="text-xs text-[#8B92B3]" dir="ltr">
                      {r.created_at ? new Date(r.created_at).toLocaleString('ar') : ""}
                    </span>
                    {!r.approved && (
                      <span className="px-2 py-0.5 rounded-md bg-red-100 text-red-700 text-[10px] font-bold border border-red-200">
                        مخفي
                      </span>
                    )}
                  </div>
                  {r.comment ? (
                    <p className="text-sm text-[#4A5078] whitespace-pre-line">{r.comment}</p>
                  ) : (
                    <p className="text-sm text-[#8B92B3] italic">— بدون تعليق —</p>
                  )}
                </div>
                <div className="flex md:flex-col items-stretch gap-2 flex-shrink-0">
                  <Button
                    size="sm"
                    variant={r.approved ? "outline" : "default"}
                    onClick={() => toggleApproved(r)}
                    className={r.approved ? "" : "bg-emerald-600 hover:bg-emerald-700 text-white"}
                    data-testid={`admin-review-toggle-${r.review_id}`}
                  >
                    {r.approved ? <><Hide className="w-4 h-4 ml-1" /> إخفاء</> : <><CheckCircle className="w-4 h-4 ml-1" /> إظهار</>}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => deleteReview(r)}
                    className="text-red-600 border-red-300 hover:bg-red-50"
                    data-testid={`admin-review-delete-${r.review_id}`}
                  >
                    <Trash2 className="w-4 h-4 ml-1" />
                    حذف
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


export default AdminPanel;
